export type ApiMethod = 'GET' | 'POST' | 'PUT' | 'DELETE';

export type ApiEndpoint = {
  name: string;
  method: ApiMethod;
  path: string;
  description?: string;
};

/** endpoint.path의 `{key}` 플레이스홀더를 params 값으로 치환한 ApiEndpoint를 반환한다. */
export function withParams(
  endpoint: ApiEndpoint,
  params: Record<string, string | number>
): ApiEndpoint {
  const path = Object.entries(params).reduce(
    (currentPath, [key, value]) => currentPath.replace(`{${key}}`, String(value)),
    endpoint.path
  );

  return { ...endpoint, path };
}

export const API_ENDPOINTS = {
  authLogin: { name: '로그인', method: 'POST', path: '/api/workbench/auths/login' },
  authEmailCodeVerify: {
    name: '이메일 2차 인증 검증',
    method: 'POST',
    path: '/api/workbench/auths/emailCode/verify',
  },
  authEmailCodeResend: {
    name: '이메일 2차 인증 코드 재전송',
    method: 'POST',
    path: '/api/workbench/auths/emailCode/resend',
  },
  authPasswordResetLink: {
    name: '비밀번호 변경 링크 발송',
    method: 'POST',
    path: '/api/workbench/auths/emailLink',
    description: '관리자가 비밀번호 초기화 링크를 발송합니다.',
  },
  authPasswordResetTokenVerify: {
    name: '비밀번호 변경 링크 토큰 검증',
    method: 'POST',
    path: '/api/workbench/auths/emailLink/verify',
  },
  authPasswordResetByEmail: {
    name: '비밀번호 변경 - 이메일',
    method: 'POST',
    path: '/api/workbench/auths/emailLink/password-reset',
    description: '현재 비밀번호를 입력하지 않습니다.',
  },
  authPasswordChange: {
    name: '비밀번호 변경 - 개인',
    method: 'POST',
    path: '/api/workbench/auths/password',
    description: '현재 비밀번호 받음',
  },
  authPasswordResetConfirm: {
    name: '비밀번호 변경 버튼',
    method: 'POST',
    path: '/api/workbench/auths/emailLink/password',
  },
  authLogout: { name: '로그아웃', method: 'POST', path: '/api/workbench/auths/logout' },
  managerList: { name: '계정 목록 조회', method: 'GET', path: '/api/workbench/managers' },
  managerMine: { name: '계정 상세 조회', method: 'GET', path: '/api/workbench/managers/mine' },
  managerCreate: { name: '계정 생성', method: 'POST', path: '/api/workbench/managers' },
  managerAccountDuplicate: {
    name: '계정 중복 확인',
    method: 'GET',
    path: '/api/workbench/managers/duplicate/account',
  },
  managerEmailDuplicate: {
    name: '이메일 중복 확인',
    method: 'GET',
    path: '/api/workbench/managers/duplicate/email',
  },
  managerUpdate: {
    name: '계정 수정',
    method: 'PUT',
    path: '/api/workbench/managers/{managerId}',
  },
  managerDelete: { name: '계정 삭제', method: 'DELETE', path: '/api/workbench/managers' },
  memberList: { name: '홈페이지 사용자 목록 조회', method: 'GET', path: '/api/workbench/members' },
  memberUpdate: {
    name: '홈페이지 사용자 수정',
    method: 'PUT',
    path: '/api/workbench/members/{memberId}',
  },
  memberDelete: {
    name: '홈페이지 사용자 삭제',
    method: 'DELETE',
    path: '/api/workbench/members/{memberId}',
  },
  goodsRepresentativeEventList: {
    name: '굿즈 대표 이미지 대회 목록 조회',
    method: 'GET',
    path: '/api/workbench/events/goods/representative',
  },
  goodsRepresentativeCreate: {
    name: '굿즈 대표 이미지 설정',
    method: 'POST',
    path: '/api/workbench/events/{eventId}/goods/representative',
  },
  goodsRepresentativeUpdate: {
    name: '굿즈 대표 이미지 수정',
    method: 'PUT',
    path: '/api/workbench/events/{eventId}/goods/representative',
  },
  goodsRepresentativeDelete: {
    name: '굿즈 대표 이미지 삭제',
    method: 'DELETE',
    path: '/api/workbench/events/{eventId}/goods/representative',
  },
  goodsList: {
    name: '굿즈 목록 조회',
    method: 'GET',
    path: '/api/public/events/{eventId}/goods',
  },
  goodsCreate: {
    name: '굿즈 추가',
    method: 'POST',
    path: '/api/workbench/events/{eventId}/goods',
  },
  goodsDelete: {
    name: '굿즈 삭제',
    method: 'DELETE',
    path: '/api/workbench/events/{eventId}/goods/{goodsId}',
  },
  readerDeviceList: {
    name: '리더기 코드 목록 조회',
    method: 'GET',
    path: '/api/workbench/reader-devices',
  },
  readerDeviceCreate: {
    name: '리더기 코드 생성',
    method: 'POST',
    path: '/api/workbench/reader-devices',
  },
  readerDeviceUpdate: {
    name: '리더기 코드 수정',
    method: 'PUT',
    path: '/api/workbench/reader-devices/{readerDeviceId}',
  },
  readerDeviceDelete: {
    name: '리더기 코드 삭제',
    method: 'DELETE',
    path: '/api/workbench/reader-devices/{readerDeviceId}',
  },
  announcementList: {
    name: '공지사항 목록 조회',
    method: 'GET',
    path: '/api/public/announcements',
  },
  announcementDetail: {
    name: '공지사항 상세 조회',
    method: 'GET',
    path: '/api/public/announcements/{announcementId}',
  },
  announcementUpdate: {
    name: '공지사항 수정',
    method: 'PUT',
    path: '/api/workbench/announcements/{announcementId}',
  },
  announcementCreate: {
    name: '공지사항 생성',
    method: 'POST',
    path: '/api/workbench/announcements',
  },
  announcementDelete: {
    name: '공지사항 삭제',
    method: 'DELETE',
    path: '/api/workbench/announcements',
  },
  certificateTemplateList: {
    name: '완주증 템플릿 목록 조회',
    method: 'GET',
    path: '/api/workbench/certificate-templates',
  },
  certificateTemplateDetail: {
    name: '완주증 템플릿 상세 조회',
    method: 'GET',
    path: '/api/workbench/certificate-templates/{certificateTemplateId}',
  },
  certificateTemplateCreate: {
    name: '완주증 템플릿 생성',
    method: 'POST',
    path: '/api/workbench/certificate-templates',
  },
  certificateTemplateUpdate: {
    name: '완주증 템플릿 수정',
    method: 'PUT',
    path: '/api/workbench/certificate-templates/{certificateTemplateId}',
  },
  certificateTemplateDelete: {
    name: '완주증 템플릿 삭제',
    method: 'DELETE',
    path: '/api/workbench/certificate-templates/{certificateTemplateId}',
  },
  certificateFontList: {
    name: '완주증 폰트 목록 조회',
    method: 'GET',
    path: '/api/workbench/certificate-fonts',
  },
  certificateFontCreate: {
    name: '완주증 폰트 등록',
    method: 'POST',
    path: '/api/workbench/certificate-fonts',
  },
  certificateFontDelete: {
    name: '완주증 폰트 삭제',
    method: 'DELETE',
    path: '/api/workbench/certificate-fonts/{certificateFontId}',
  },
  eventList: { name: '대회 목록 조회', method: 'GET', path: '/api/workbench/events' },
  eventCreate: { name: '대회 생성', method: 'POST', path: '/api/workbench/events' },
  eventDetail: { name: '대회 상세 정보', method: 'GET', path: '/api/workbench/events/{eventId}' },
  eventUpdate: { name: '대회 정보 수정', method: 'PUT', path: '/api/workbench/events/{eventId}' },
  eventDelete: { name: '대회 삭제', method: 'DELETE', path: '/api/workbench/events/{eventId}' },
  eventDuplicate: {
    name: '대회 복제',
    method: 'POST',
    path: '/api/workbench/events/{eventId}/duplicate',
  },
  timingPointCreate: {
    name: '계측 지점 생성',
    method: 'POST',
    path: '/api/workbench/events/{eventId}/timing-points',
  },
  timingPointList: {
    name: '계측 지점 목록 조회',
    method: 'GET',
    path: '/api/workbench/events/{eventId}/timing-points',
  },
  timingPointUpdate: {
    name: '계측 지점 수정',
    method: 'PUT',
    path: '/api/workbench/events/{eventId}/timing-points/{timingPointId}',
  },
  timingPointDelete: {
    name: '계측 지점 삭제',
    method: 'DELETE',
    path: '/api/workbench/events/{eventId}/timing-points/{timingPointId}',
  },
  categoryCreate: {
    name: '종목 생성',
    method: 'POST',
    path: '/api/workbench/events/{eventId}/categories',
  },
  categoryList: {
    name: '종목 목록 조회',
    method: 'GET',
    path: '/api/workbench/events/{eventId}/categories',
    description: '종목의 그룹도 같이 조회된다.',
  },
  categoryUpdate: {
    name: '종목 이름 수정',
    method: 'PUT',
    path: '/api/workbench/events/{eventId}/categories/{categoryId}',
  },
  categoryDelete: {
    name: '종목 삭제',
    method: 'DELETE',
    path: '/api/workbench/events/{eventId}/categories/{categoryId}',
  },
  groupCreate: {
    name: '그룹 생성',
    method: 'POST',
    path: '/api/workbench/events/{eventId}/categories/{categoryId}/groups',
  },
  groupUpdate: {
    name: '그룹 수정',
    method: 'PUT',
    path: '/api/workbench/events/{eventId}/categories/{categoryId}/groups/{groupId}',
  },
  groupDelete: {
    name: '그룹 삭제',
    method: 'DELETE',
    path: '/api/workbench/events/{eventId}/categories/{categoryId}/groups/{groupId}',
  },
  splitCreate: {
    name: '구간 생성',
    method: 'POST',
    path: '/api/workbench/events/{eventId}/splits',
  },
  splitList: {
    name: '구간 목록 조회',
    method: 'GET',
    path: '/api/workbench/events/{eventId}/splits',
  },
  splitUpdate: {
    name: '구간 수정',
    method: 'PUT',
    path: '/api/workbench/events/{eventId}/splits/{splitId}',
  },
  splitDelete: {
    name: '구간 삭제',
    method: 'DELETE',
    path: '/api/workbench/events/{eventId}/splits/{splitId}',
  },
  participantCreate: {
    name: '참가자 단일 삽입',
    method: 'POST',
    path: '/api/workbench/events/{eventId}/participants',
  },
  participantImportFile: {
    name: '참가자 파일 삽입',
    method: 'POST',
    path: '/api/workbench/events/{eventId}/participants/import-file',
  },
  participantList: {
    name: '참자가 목록 조회',
    method: 'GET',
    path: '/api/workbench/events/{eventId}/participants',
  },
  participantUpdate: {
    name: '참가자 수정',
    method: 'PUT',
    path: '/api/workbench/events/{eventId}/participants/{participantId}',
  },
  participantDelete: {
    name: '참가자 삭제',
    method: 'DELETE',
    path: '/api/workbench/events/{eventId}/participants',
  },
  participantDeleteAll: {
    name: '참가자 전체 삭제',
    method: 'DELETE',
    path: '/api/workbench/events/{eventId}/participants/all',
  },
  participantSearch: {
    name: '참가자 검색',
    method: 'GET',
    path: '/api/workbench/events/{eventId}/participants',
  },
  participantCsvDownload: {
    name: '참가자 목록 CSV 다운로드',
    method: 'GET',
    path: '/api/workbench/events/{eventId}/participants/export-file',
  },
  eventReaderDeviceList: {
    name: '대회 리더기 목록',
    method: 'GET',
    path: '/api/workbench/events/{eventId}/reader-devices',
  },
  eventReaderDeviceCreate: {
    name: '대회 리더기 추가',
    method: 'POST',
    path: '/api/workbench/events/{eventId}/reader-devices',
  },
  eventReaderDeviceTimingPointUpdate: {
    name: '대회 리더기 계측 지점 설정',
    method: 'PUT',
    path: '/api/workbench/events/{eventId}/reader-devices/{readerDeviceId}',
  },
  eventReaderDeviceDelete: {
    name: '대회 리더기 삭제',
    method: 'DELETE',
    path: '/api/workbench/events/{eventId}/reader-devices/{readerDeviceId}',
  },
  readerDeviceCurrentEventSet: {
    name: '리더기에 현재 대회 설정',
    method: 'PUT',
    path: '/api/workbench/events/{eventId}/reader-devices/{readerDeviceId}/config',
  },
  eventReaderDevicesActivate: {
    name: '대회 리더기 전체 현재 대회 활성화',
    method: 'PUT',
    path: '/api/workbench/events/{eventId}/reader-devices/active',
  },
  eventReaderDevicesDeactivate: {
    name: '대회 리더기 전체 현재 대회 비활성화',
    method: 'PUT',
    path: '/api/workbench/events/{eventId}/reader-devices/deactive',
  },
  statisticSummary: {
    name: '참가자 연령 통계 조회',
    method: 'GET',
    path: '/api/workbench/events/{eventId}/graph/statistic',
  },
  statisticSplitGraph: {
    name: '참가자 별 구간 통계',
    method: 'GET',
    path: '/api/workbench/events/{eventId}/graph/split',
  },
  statisticLivePosition: {
    name: '실시간 참가자 위치 현황 조회',
    method: 'GET',
    path: '/api/workbench/events/{eventId}/graph/live',
  },
  statisticCumulativeAnalysis: {
    name: '누적 참가자 구간 통과 통계 조회',
    method: 'GET',
    path: '/api/workbench/events/{eventId}/graph/cumulative',
  },
  leaderboard: {
    name: '리더보드 조회',
    method: 'GET',
    path: '/api/workbench/events/{eventId}/graph/leaderboard',
  },
  leaderboardExport: {
    name: '리더보드 출력 파일 다운로드',
    method: 'GET',
    path: '/api/workbench/events/{eventId}/graph/leaderboard/export',
  },
  resultList: {
    name: '결과 목록 조회',
    method: 'GET',
    path: '/api/workbench/events/{eventId}/results',
  },
  resultParticipantDetail: {
    name: '참가자 결과 상세 조회',
    method: 'GET',
    path: '/api/workbench/events/{eventId}/results/{participantId}',
  },
  resultUpdate: {
    name: '결과 수정',
    method: 'PUT',
    path: '/api/workbench/events/{eventId}/results/{participantId}',
  },
  resultDownload: {
    name: '결과 다운로드',
    method: 'POST',
    path: '/api/workbench/events/{eventId}/results/download',
  },
  resultNeedsReview: {
    name: '검토필요 상태 존재 여부 조회',
    method: 'GET',
    path: '/api/workbench/events/{eventId}/results/needs-review',
  },
  rawDataList: {
    name: '원시 데이터 조회',
    method: 'GET',
    path: '/api/workbench/events/{eventId}/raw-data',
  },
  b2bList: { name: 'B2B 문의 목록 조회', method: 'GET', path: '/api/workbench/b2bs' },
  b2bDetail: { name: 'B2B 문의 상세 조회', method: 'GET', path: '/api/workbench/b2bs/{b2bId}' },
  b2bAnswerCreate: {
    name: 'B2B 문의 답변 작성',
    method: 'POST',
    path: '/api/workbench/b2bs/{b2bId}/answer',
  },
  b2bAnswerUpdate: {
    name: 'B2B 문의 답변 수정',
    method: 'PUT',
    path: '/api/workbench/b2bs/{b2bId}/answer',
  },
  b2bDelete: { name: 'B2B 문의 삭제', method: 'DELETE', path: '/api/workbench/b2bs' },
  qnaList: { name: 'QnA 목록 조회', method: 'GET', path: '/api/workbench/qnas' },
  qnaDetail: { name: 'QnA 상세 조회', method: 'GET', path: '/api/workbench/qnas/{qnaId}' },
  qnaAnswerCreate: {
    name: 'QnA 답변 작성',
    method: 'POST',
    path: '/api/workbench/qnas/{qnaId}/answer',
  },
  qnaAnswerUpdate: {
    name: 'QnA 답변 수정',
    method: 'PUT',
    path: '/api/workbench/qnas/{qnaId}/answer',
  },
  qnaDelete: { name: 'QnA 삭제', method: 'DELETE', path: '/api/workbench/qnas' },
  epcUpload: { name: 'EPC 데이터 업로드', method: 'GET', path: '/cpvUpload2' },
  readerHeartbeat: {
    name: '리더기 연결 테스트 / 하트비트',
    method: 'GET',
    path: '/testConnection',
  },
  eventConfigFile: {
    name: '이벤트 설정파일 조회',
    method: 'GET',
    path: '/getEventConfigfile/{machineId}',
  },
  eventConfigFileDownload: {
    name: '이벤트 설정파일 다운로드',
    method: 'GET',
    path: '/ec_{eventId}.ecg',
  },
} as const satisfies Record<string, ApiEndpoint>;

export type ApiEndpointKey = keyof typeof API_ENDPOINTS;
